<template>
  <div class="tw:rounded-md tw:border tw:border-solid tw:border-border-secondary tw:bg-white">
    <div class="tw:w-full tw:overflow-x-auto tw:rounded-t-md">
      <table class="tw:w-full tw:border-collapse tw:text-14 tw:text-gray-900">
        <thead class="tw:align-bottom">
          <tr>
            <th
              v-for="col in columns"
              :key="col.key"
              :style="col.maxWidth != null ? { maxWidth: `${col.maxWidth}px` } : undefined"
              :class="
                cn(
                  'tw:border-r! tw:border-b! tw:border-solid! tw:border-border-secondary! tw:bg-gray-50 tw:px-4 tw:py-2',
                  'tw:text-left tw:text-11 tw:font-medium tw:tracking-wider tw:whitespace-nowrap tw:text-gray-500 tw:uppercase',
                  'tw:last:border-r-0! tw:first:rounded-tl-md tw:last:rounded-tr-md',
                  col.shrink && 'tw:w-px',
                  col.align === 'right' && 'tw:text-right',
                  col.align === 'center' && 'tw:text-center',
                  col.sticky === 'right' &&
                    'tw:sticky tw:right-0 tw:z-10 tw:border-l! tw:shadow-[-1px_0_0_0_var(--color-border-secondary)]',
                  col.sticky === 'left' &&
                    'tw:sticky tw:left-0 tw:z-10 tw:border-r! tw:shadow-[1px_0_0_0_var(--color-border-secondary)]',
                  col.headerClass
                )
              ">
              <button
                v-if="col.sortKey"
                type="button"
                :aria-sort="ariaSort(col)"
                :class="
                  cn(
                    'group/sort tw:inline-flex tw:cursor-pointer tw:items-center tw:border-0 tw:bg-transparent tw:p-0',
                    'tw:text-inherit tw:uppercase! tw:no-underline'
                  )
                "
                @click="onSort(col)">
                <span>{{ col.label }}</span>
                <IconChevronDown
                  :size="14"
                  :stroke-width="2"
                  :class="
                    cn(
                      'tw:ml-1 tw:transition-[opacity,transform] tw:duration-200 tw:motion-reduce:transition-none',
                      isActiveSort(col) ? 'tw:opacity-100' : 'tw:opacity-0 tw:group-hover/sort:opacity-50',
                      isActiveSort(col) && sortDir === 'asc' && 'tw:rotate-180'
                    )
                  " />
              </button>
              <template v-else>{{ col.label }}</template>
            </th>
          </tr>
        </thead>

        <tbody v-if="loading">
          <tr v-for="(sk, i) in skeletonRows" :key="`loading-${i}`">
            <!-- first cell: avatar + two stacked bars -->
            <td class="tw:border-b! tw:border-solid! tw:border-border-secondary! tw:px-4 tw:py-3.5">
              <div class="tw:flex tw:items-center tw:gap-4">
                <LeadSkeleton variant="block" :width="32" :height="32" class="tw:shrink-0" />
                <span class="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-2">
                  <LeadSkeleton :width="sk.w1" :height="10" />
                  <LeadSkeleton :width="sk.w2" :height="8" />
                </span>
              </div>
            </td>
            <!-- remaining cells: single bar, aligned like the real column -->
            <td
              v-for="col in columns.slice(1)"
              :key="col.key"
              :class="
                cn(
                  'tw:border-b! tw:border-solid! tw:border-border-secondary! tw:px-4 tw:py-3.5',
                  col.align === 'right' && 'tw:text-right',
                  col.align === 'center' && 'tw:text-center',
                  col.sticky === 'right' &&
                    'tw:sticky tw:right-0 tw:z-10 tw:bg-white tw:shadow-[-1px_0_0_0_var(--color-border-secondary)]',
                  col.sticky === 'left' &&
                    'tw:sticky tw:left-0 tw:z-10 tw:bg-white tw:shadow-[1px_0_0_0_var(--color-border-secondary)]'
                )
              ">
              <LeadSkeleton
                :width="col.shrink ? 24 : 64"
                :height="10"
                :class="
                  cn('tw:inline-block', col.align === 'right' && 'tw:ml-auto', col.align === 'center' && 'tw:mx-auto')
                " />
            </td>
          </tr>
          <tr>
            <td :colspan="columns.length" class="tw:px-4 tw:py-4 tw:text-center tw:text-13 tw:text-muted">
              <span class="tw:inline-flex tw:items-center tw:justify-center tw:gap-2">
                <span role="status" aria-label="Loading" class="tw:inline-block tw:size-6">
                  <span
                    class="tw:block tw:size-full tw:animate-spin tw:rounded-full tw:border-2 tw:border-solid tw:border-current tw:border-r-transparent tw:motion-reduce:animate-none" />
                </span>
                {{ loadingLabel }}
              </span>
            </td>
          </tr>
        </tbody>

        <tbody v-else-if="rows.length === 0">
          <tr>
            <td :colspan="columns.length" class="tw:px-4 tw:py-10 tw:text-center tw:text-gray-500">
              <slot name="empty">{{ emptyLabel }}</slot>
            </td>
          </tr>
        </tbody>

        <tbody v-else>
          <tr v-for="(row, index) in rows" :key="rowKey(row, index)" class="group tw:hover:bg-gray-25">
            <td
              v-for="col in columns"
              :key="col.key"
              :style="col.maxWidth != null ? { maxWidth: `${col.maxWidth}px` } : undefined"
              :class="
                cn(
                  'tw:border-b! tw:border-solid! tw:border-border-secondary! tw:px-4 tw:py-3 tw:align-middle',
                  col.noWrap && 'tw:whitespace-nowrap',
                  col.maxWidth != null && 'tw:overflow-hidden',
                  col.align === 'right' && 'tw:text-right',
                  col.align === 'center' && 'tw:text-center',
                  col.sticky === 'right' &&
                    'tw:sticky tw:right-0 tw:z-10 tw:bg-white tw:group-hover:bg-gray-25! tw:shadow-[-1px_0_0_0_var(--color-border-secondary)]',
                  col.sticky === 'left' &&
                    'tw:sticky tw:left-0 tw:z-10 tw:bg-white tw:group-hover:bg-gray-25! tw:shadow-[1px_0_0_0_var(--color-border-secondary)]',
                  col.cellClass
                )
              ">
              <slot :name="`cell-${col.key}`" :row="row" :column="col" :index="index">
                <slot name="cell" :row="row" :column="col" :index="index">
                  {{ defaultCellText(row, col) }}
                </slot>
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-if="showFooter && !loading && !(hideWhenSinglePage && pageCount <= 1)"
      class="tw:flex tw:items-center tw:bg-gray-50 tw:px-4 tw:py-3 tw:rounded-b-md">
      <LeadPagination
        :page="page"
        :page-count="pageCount"
        :total="total"
        :page-size="pageSize"
        :item-label="itemLabel"
        :hide-when-single-page="hideWhenSinglePage"
        class="tw:w-full"
        @update:page="(p) => emit('update:page', p)" />
    </div>
  </div>
</template>

<script setup lang="ts" generic="TRow extends Record<string, any>">
import { computed } from 'vue';
import { IconChevronDown } from '@tabler/icons-vue';
import LeadPagination from '@/components/lead-design/LeadPagination.vue';
import LeadSkeleton from '@/components/lead-design/LeadSkeleton.vue';
import { cn } from '@/lib/cn';

export interface LeadTableColumn {
  /** Stable identifier — also used for the `cell.<key>` slot name. */
  key: string;
  /** Header label. */
  label: string;
  /** Sort key passed back on sort. If omitted, the column isn't sortable. */
  sortKey?: string;
  /** Collapse to content width. */
  shrink?: boolean;
  /** Prevent line wrapping in body cells. */
  noWrap?: boolean;
  /**
   * Max column width in px. Body cells add `truncate` so overflowing content ellipses;
   * consumers with multi-line content should apply their own truncation inside their cell slot.
   */
  maxWidth?: number;
  align?: 'left' | 'right' | 'center';
  headerClass?: string;
  cellClass?: string;
  /** Field to render in the default cell when no slot is provided. */
  field?: string;
  /**
   * Pin the column to the left or right edge when the table scrolls horizontally.
   * Sticky cells get a solid background and a divider on the inward edge so the
   * pinned column reads as a separate layer from the scrolling content.
   */
  sticky?: 'left' | 'right';
}

const props = withDefaults(
  defineProps<{
    columns: LeadTableColumn[];
    rows: TRow[];
    /** Uniquely identifies a row. Defaults to `row.id`, falling back to index. */
    rowKey?: (row: TRow, index: number) => string | number;
    loading?: boolean;
    /** Label shown next to the spinner row while loading. */
    loadingLabel?: string;
    /** Number of skeleton rows to render while loading. Defaults to 5. */
    loadingRowCount?: number;
    emptyLabel?: string;
    /** Server-side sort state — the currently sorted column's sortKey. */
    sortKey?: string;
    sortDir?: 'asc' | 'desc';
    /* pagination */
    page?: number;
    pageCount?: number;
    total?: number;
    pageSize?: number;
    itemLabel?: string;
    showFooter?: boolean;
    hideWhenSinglePage?: boolean;
  }>(),
  {
    rowKey: (row: any, index: number) => row?.id ?? index,
    loading: false,
    loadingLabel: 'Loading…',
    loadingRowCount: 5,
    emptyLabel: 'No results',
    sortKey: undefined,
    sortDir: 'desc',
    page: 1,
    pageCount: 1,
    total: 0,
    pageSize: 10,
    itemLabel: undefined,
    showFooter: true,
    hideWhenSinglePage: true,
  }
);

const emit = defineEmits<{
  (e: 'update:page', page: number): void;
  // `null` payload signals a reset: the third click on the active column
  // clears the sort so the consumer can restore its own default.
  (e: 'update:sort', payload: { key: string; direction: 'asc' | 'desc' } | null): void;
}>();

/* --------------------------------- COMPUTED ------------------------------- */

// Varied widths so the loading state doesn't read as a striped bar chart.
// Widths mirror the OrdersRedesign spec's SKELETON list; cycled to fit `loadingRowCount`.
const SKELETON_WIDTHS = [
  { w1: '60%', w2: '30%' },
  { w1: '45%', w2: '25%' },
  { w1: '70%', w2: '35%' },
  { w1: '40%', w2: '20%' },
  { w1: '55%', w2: '28%' },
];
const skeletonRows = computed(() =>
  Array.from({ length: Math.max(0, props.loadingRowCount) }, (_, i) => SKELETON_WIDTHS[i % SKELETON_WIDTHS.length])
);

/* --------------------------------- METHODS -------------------------------- */

function isActiveSort(col: LeadTableColumn) {
  return !!col.sortKey && col.sortKey === props.sortKey;
}

function ariaSort(col: LeadTableColumn): 'ascending' | 'descending' | undefined {
  if (!isActiveSort(col)) return undefined;
  return props.sortDir === 'asc' ? 'ascending' : 'descending';
}

// Three-state cycle: click once for asc, again for desc, a third time to reset.
// Clicking a different column starts fresh at asc.
function onSort(col: LeadTableColumn) {
  if (!col.sortKey) return;
  if (isActiveSort(col)) {
    if (props.sortDir === 'asc') {
      emit('update:sort', { key: col.sortKey, direction: 'desc' });
    } else {
      emit('update:sort', null);
    }
    return;
  }
  emit('update:sort', { key: col.sortKey, direction: 'asc' });
}

function defaultCellText(row: TRow, col: LeadTableColumn) {
  if (!col.field) return '';
  return row?.[col.field] ?? '';
}
</script>
